/** An `HH:MM` band, or nothing at all when the user doesn't care. */
export interface TimeBand {
  start: string;
  end: string;
}

/** Every quarter hour of the day, as `HH:MM`. */
const STEPS: string[] = Array.from({ length: 24 * 4 }, (_, i) => {
  const h = `${Math.floor(i / 4)}`.padStart(2, "0");
  const m = `${(i % 4) * 15}`.padStart(2, "0");
  return `${h}:${m}`;
});

/**
 * The hours a meeting may fall in, on each of the chosen days.
 *
 * <p>The time is a <em>granularity over the date</em>: pick the days first, then
 * narrow to a band within them. Leave it off entirely and the days stand alone —
 * the agents use the initiator's availability-pattern hours.</p>
 *
 * <p>Two quarter-hour dropdowns rather than a native `<input type="time">`, which
 * made you scroll hours and minutes separately, twice.</p>
 */
export function TimeRangePicker({
  value,
  onChange,
  currentBand,
  durationMinutes,
}: {
  value: TimeBand | null;
  onChange: (band: TimeBand | null) => void;
  /** The meeting's current hours — what the band resets to when re-enabled. */
  currentBand?: TimeBand | null;
  durationMinutes?: number | null;
}) {
  const invalid = !!value && value.start >= value.end;

  // Keep the band at least as long as the meeting: a 30-minute meeting cannot
  // fit in a 20-minute window, and a strict request like that can only fail.
  const tooShort =
    !!value &&
    !invalid &&
    !!durationMinutes &&
    minutesBetween(value.start, value.end) < durationMinutes;

  const fallback: TimeBand = currentBand ?? { start: "09:00", end: "10:00" };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="uppercase tracking-wide text-muted-foreground">Between</span>
        <StepSelect
          label="Window start time"
          value={value ? value.start : fallback.start}
          disabled={!value}
          onChange={(start) => onChange({ start, end: (value ?? fallback).end })}
        />
        <span className="text-muted-foreground">and</span>
        <StepSelect
          label="Window end time"
          value={value ? value.end : fallback.end}
          disabled={!value}
          onChange={(end) => onChange({ start: (value ?? fallback).start, end })}
        />
      </div>

      <label className="flex w-fit items-center gap-1.5 text-[11px] text-muted-foreground">
        <input
          type="checkbox"
          checked={value === null}
          onChange={(e) => onChange(e.target.checked ? null : fallback)}
          className="h-3.5 w-3.5 accent-primary"
        />
        Any time on those days
      </label>

      {invalid && (
        <span className="text-[11px] font-medium text-destructive">
          The end must be after the start.
        </span>
      )}
      {tooShort && (
        <span className="text-[11px] font-medium text-destructive">
          That window is shorter than the {durationMinutes}-minute meeting.
        </span>
      )}
    </div>
  );
}

/** True when the picked band can actually be asked for. */
export function bandIsUsable(
  band: TimeBand | null,
  durationMinutes?: number | null,
): boolean {
  if (!band) return true;
  if (band.start >= band.end) return false;
  if (durationMinutes && minutesBetween(band.start, band.end) < durationMinutes) {
    return false;
  }
  return true;
}

function StepSelect({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  // A time the meeting already uses may not sit on the quarter-hour grid, so
  // offer it too rather than snapping the user's own booking away from them.
  const options = STEPS.includes(value)
    ? STEPS
    : [...STEPS, value].sort((a, b) => a.localeCompare(b));
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-input bg-background px-1.5 py-1 text-xs disabled:opacity-40"
    >
      {options.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

function minutesBetween(start: string, end: string): number {
  return toMinutes(end) - toMinutes(start);
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}
